import _ from 'lodash';
import { PAGE, PRODUCT, PRODUCT_GROUP, SUB_MENU, SUB_MENU_LIST } from '../constants/MenuItemTypes';
export function moneyFormat(price) {
  return (
    '£' +
    Number(price)
      .toFixed(2)
      .replace(/\d(?=(\d{3})+\.)/g, '$&,')
  );
}

export function quoteTotal(quote) {
  const menuCost = _.sumBy(_.filter(quote.menu, { selected: true }), 'total');
  return menuCost + Number(quote.selectedDate.price || 0) + Number(quote.eventType.fee || 0);
}
export function getAllChildItems(item, menu) {
  let items = _.filter(menu, function (i) {
    return i._lft >= item._lft && i._rgt <= item._rgt;
  });
  return _.orderBy(items, '_lft');
}

export function getAllItemsInAGroup(item, menu) {
  let parent = getParent(item, menu);
  return getAllChildItems(parent, menu);
}

export function getParent(item, menu) {
  return _.find(menu, { id: item.parent_id });
}
export function getSameLevelItems(item, menu, additional) {
  return _.filter(menu, { parent_id: item.parent_id, ...additional});
}

const menuSectionLabel = (item) => (item.label || '').normalize('NFD')
  .replace(/[\u0300-\u036f'’]/g, '').trim().toLowerCase();

export function getMainMenuOption(item, menu) {
  let option = item;
  while (option && option.parent_id) {
    const parent = getParent(option, menu);
    if (!parent) return null;
    const label = menuSectionLabel(option);
    // The API has no main-menu role field. These are the main section names in
    // the retained venue payloads; course, wine and evening-food groups differ.
    if (parent.menu_item_type_id === PRODUCT_GROUP
      && ['menus', 'lets feast'].includes(menuSectionLabel(parent))) {
      // Older payloads place an independent canapé package inside Menus.
      return [SUB_MENU, SUB_MENU_LIST].includes(option.menu_item_type_id)
        && !label.startsWith('canap') ? option : null;
    }
    if (parent.menu_item_type_id === PAGE && option.menu_item_type_id === PRODUCT_GROUP) {
      const optionId = option.id;
      const courses = getAllChildItems(option, menu).filter(child => (
        child.parent_id === optionId && [SUB_MENU, SUB_MENU_LIST].includes(child.menu_item_type_id)
      ));
      // Ivy Lodge represents the regular meal as a group of course dialogs,
      // and its Feast as a separate group. The courses are one menu together.
      if ((label === 'the menu' && courses.some(course => menuSectionLabel(course) === 'mains'))
        || (label.endsWith(' feast') && courses.length === 1
          && courses[0].menu_item_type_id === SUB_MENU_LIST && menuSectionLabel(courses[0]) === label)) {
        return option;
      }
      return null;
    }
    option = parent;
  }
  return null;
}

export function isRequirementUnmet(item, menu) {
  let count = _.filter(getAllChildItems(item, menu), (f) => f.selected === true
    && f.menu_item_type_id === PRODUCT
    && f.id !== item.id).length;
  if (count >= item.minimum_choices || item.minimum_choices === 0) {
    return false;
  }
  // a SUB_MENU parent is optional, so only count this as unmet once the parent menu itself is selected
  if (menu[item.parent_id] && menu[item.parent_id].menu_item_type_id !== SUB_MENU) {
    return true;
  }
  return !!(menu[item.parent_id] && menu[item.parent_id].selected);
}

export function getUnmetRequirements(menu) {
  return _.orderBy(
    _.filter(menu, (i) => i.minimum_choices !== null && i.minimum_choices !== 0 && isRequirementUnmet(i, menu)),
    '_lft',
  );
}

export function unmetRequirementsMessage(items) {
  if (items.length === 0) {
    return 'Please choose your menu items to continue';
  }
  if (items.length === 1) {
    return `Please complete ${items[0].label} to continue`;
  }
  if (items.length === 2) {
    return `Please complete ${items[0].label} and ${items[1].label} to continue`;
  }
  if (items.length === 3) {
    return `Please complete ${items[0].label}, ${items[1].label} and ${items[2].label} to continue`;
  }
  return `Please complete ${items[0].label}, ${items[1].label}, ${items[2].label} and ${items.length - 3} more to continue`;
}

export function requiredItemsLabel(item) {
  return `Choose ${item.minimum_choices}`;
}

export function isInclusiveGroup(item, menu) {
  const children = _.filter(getAllChildItems(item, menu), (c) => c.id !== item.id && c.menu_item_type_id === PRODUCT);
  return children.length > 0 && _.every(children, (c) => c.unit_price === null || Number(c.unit_price) === 0);
}

export function maximumChoicesLabel(item, menu) {
  if (isInclusiveGroup(item, menu)) {
    return `${item.maximum_choices} included`;
  }
  return item.maximum_choices === 1 ? 'Choose 1' : `Choose up to ${item.maximum_choices}`;
}

export function choiceLimitMessage(item, menu) {
  const parent = getParent(item, menu);
  if (item.selected || !parent || !(parent.maximum_choices > 1)) return '';
  const selected = getAllChildItems(parent, menu).filter((child) => (
    child.id !== parent.id && child.depth === parent.depth + 1 && child.selected === true
  ));
  if (selected.length < parent.maximum_choices) return '';
  return `You can choose up to ${parent.maximum_choices} for ${parent.label}. Remove a selected option first.`;
}

export function menuRequirement(item, menu) {
  if (!(item.minimum_choices > 0)) return null;
  const selected = getAllChildItems(item, menu).filter((child) => (
    child.id !== item.id && child.selected === true
  )).length;
  // Keep the menu dialog's existing required-choice rule; expose its reason.
  return selected < item.minimum_choices
    ? { item, needed: item.minimum_choices - selected } : null;
}

export function remainingMenuRequirements(item, menu) {
  return getAllChildItems(item, menu).map((group) => menuRequirement(group, menu)).filter(Boolean);
}

export function menuRequirementLabel(requirement) {
  return `Choose ${requirement.needed} more ${requirement.needed === 1 ? 'option' : 'options'} from ${requirement.item.label}`;
}

export function getAnchorIds(item, menu) {
  let ids = [`MenuItem${item.id}`];
  let ancestor = getParent(item, menu);
  while (ancestor && ancestor.parent_id) {
    ids.push(`MenuItem${ancestor.id}`);
    ancestor = getParent(ancestor, menu);
  }
  return ids;
}
